import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db, appId } from './firebase-config';
import { collection, onSnapshot } from 'firebase/firestore';
import { Toaster } from 'react-hot-toast';

// Componentes principais
import AuthPage from './components/AuthPage';
import Dashboard from './components/Dashboard';
import Processos from './components/Processos';
import Tarefas from './components/Tarefas';
import Sidebar from './components/Sidebar'; 

// Componentes de prazos (modais e páginas)
import PrazoCalculator from './components/prazos/PrazoCalculator';
import FeriadosManager from './components/prazos/FeriadosManager';
import SuspensoesPrazo from './components/prazos/SuspensoesPrazo';
import PrazoDashboard from './components/prazos/PrazoDashboard';
import PrazoCalendario from './components/prazos/PrazoCalendario';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard');

  // Estados para controlar a visibilidade dos modais
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [isFeriadosOpen, setIsFeriadosOpen] = useState(false);
  const [isSuspensoesOpen, setIsSuspensoesOpen] = useState(false);
  
  // Estados para dados
  const [feriados, setFeriados] = useState([]);
  const [suspensoes, setSuspensoes] = useState([]);
  const [prazoParaEditar, setPrazoParaEditar] = useState(null);


  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // Listener para feriados
        const feriadosRef = collection(db, `artifacts/${appId}/users/${currentUser.uid}/feriados`);
        const unsubscribeFeriados = onSnapshot(feriadosRef, (snapshot) => {
            const feriadosData = snapshot.docs.map(doc => {
                const data = doc.data();
                return { 
                    id: doc.id, 
                    ...data,
                    data: data.data?.toDate ? data.data.toDate() : null 
                };
            });
            setFeriados(feriadosData);
        });

        // Listener para suspensões
        const suspensoesRef = collection(db, `artifacts/${appId}/users/${currentUser.uid}/suspensoes`);
        const unsubscribeSuspensoes = onSnapshot(suspensoesRef, (snapshot) => {
            const suspensoesData = snapshot.docs.map(doc => {
                const data = doc.data();
                return { 
                    id: doc.id, 
                    ...data,
                    dataInicio: data.dataInicio?.toDate ? data.dataInicio.toDate() : null,
                    dataFim: data.dataFim?.toDate ? data.dataFim.toDate() : null
                };
            });
            setSuspensoes(suspensoesData);
        });
        
        setLoading(false);
        return () => {
            unsubscribeFeriados();
            unsubscribeSuspensoes();
        };
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribeAuth();
  }, [appId]);

  const handleLogout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const handleOpenCalculator = (prazo = null) => {
    setPrazoParaEditar(prazo);
    setIsCalculatorOpen(true);
  };
  
  const renderActiveView = () => {
    switch (activeView) {
      case 'dashboard':
        return <Dashboard />;
      case 'processos':
        return <Processos />;
      case 'tarefas':
        return <Tarefas />;
      case 'calendario':
        return <PrazoCalendario />;
      case 'prazos':
         return <PrazoDashboard onEditPrazo={handleOpenCalculator} onOpenCalculator={() => handleOpenCalculator(null)} />;
      default:
        return <Dashboard />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-dark-navy">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary-gold"></div>
      </div>
    );
  }

  if (!user) {
    return <AuthPage />;
  }

  return (
    <div className="bg-dark-navy text-white min-h-screen">
      <Toaster position="top-right" />
      <header className="glass-effect border-b border-white border-opacity-20 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <div className="gold-gradient p-3 rounded-lg"><i className="fas fa-balance-scale text-2xl text-gray-900"></i></div>
                    <div>
                        <h1 className="heading-font text-2xl font-bold">JurisTask</h1>
                        <p className="text-sm text-gray-300">Sistema de Gestão Jurídica Premium</p>
                    </div>
                </div>
                <div className="flex items-center space-x-6">
                    <div className="search-bar rounded-full px-4 py-2 flex items-center space-x-3 w-96">
                        <i className="fas fa-search text-gray-400"></i>
                        <input type="text" placeholder="Buscar processos, tarefas..." className="bg-transparent flex-1 outline-none text-white placeholder-gray-400"/>
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-3">
                            <div className="text-right">
                                <p className="font-semibold">{user.email}</p>
                            </div>
                            <div className="w-10 h-10 gold-gradient rounded-full flex items-center justify-center"><i className="fas fa-user text-gray-900"></i></div>
                             <button onClick={handleLogout} className="p-2 rounded-full hover:bg-red-500 hover:bg-opacity-20 transition-colors" title="Sair">
                                <i className="fas fa-sign-out-alt text-xl"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </header>
      
      <div className="flex">
        <Sidebar 
          activeView={activeView} 
          setActiveView={setActiveView} 
          onOpenCalculator={() => handleOpenCalculator(null)}
          onOpenFeriados={() => setIsFeriadosOpen(true)}
          onOpenSuspensoes={() => setIsSuspensoesOpen(true)}
        />
        <main className="flex-1 p-6 main-content">
          {renderActiveView()}
        </main>
      </div>

      <PrazoCalculator 
          isOpen={isCalculatorOpen}
          onClose={() => setIsCalculatorOpen(false)}
          prazoParaEditar={prazoParaEditar}
          feriados={feriados}
          suspensoes={suspensoes}
      />
      <FeriadosManager
          isOpen={isFeriadosOpen}
          onClose={() => setIsFeriadosOpen(false)}
          feriados={feriados}
      />
      <SuspensoesPrazo
          isOpen={isSuspensoesOpen}
          onClose={() => setIsSuspensoesOpen(false)}
          suspensoes={suspensoes}
      />
    </div>
  );
}

export default App;
